import React, {useEffect, useMemo, useState} from 'react';

import {Button, Checkbox, Input, Spinner, VStack, NumberInput} from '@chakra-ui/react';

import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field, FilesInput, LanguageField, NestedSelect, RichText} from "@/components";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";

export const CreateOrEditResource = (props) => {
    const resourceId = props?.id;

    const [state, setState] = useState(-1);
    const {getLangObject, language, t} = useLanguage();
    const [resource, setResource] = useState(null);
    const [categories, setCategories] = useState(null);
    const [removeDeletedAt, setRemoveDeletedAt] = useState(false);
    const [defaultLanguage, setDefaultLanguage] = useState(null);
    const api = useApi();
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            title: getLangObject(),
            content: getLangObject(),
            categoryId: null,
            files: [],
            sortOrder: 0
        },
        {
            title: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            content: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((value) => value && value.length >= 20)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            categoryId: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField')
                }
            },
        }
    );

    useEffect(() => {
        setState(() => 0);
        api.ApiService.get('category', {
            getAll: 'bool:true'
        })
            .then((res) => {
                if (res?.data?.type === 'success' && res?.data?.value) {
                    let tmp = objectUtil.plainToTree(res.data.value ?? [], 'id', 'parent');
                    tmp = objectUtil.treeToPlain(tmp, 'children', 'id', 'parent');
                    setCategories(() => tmp);
                }
            })
        if (resourceId) {
            api.ApiService.getOne('resource', resourceId, {
                attributes: ['id', 'title', 'content', 'categoryId', 'files', 'sortOrder', 'createdAt', 'updatedAt'],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setResource(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (resource !== null) {
            handleInputChanges({
                ...resource,
                createdAt: new Date(resource?.createdAt),
                updatedAt: new Date(resource?.updatedAt),
            });
            setDefaultLanguage(resource.title.fr.length > 0 ? 'fr' : 'en')
        }
        /** If categories are retrieved, and we're in create mode
         * or resource and categories are retrieved, and we're in edit mode
         */
        if ((!resourceId || resource !== null) && categories !== null) {
            setState(() => 1)
        }
    }, [resource, categories]);

    const categoriesTree = useMemo(() => {
        return (categories ?? []).map((e) => ({...e, value: e.id, label: e?.[`${language}Label`] ?? ''}))
    }, [categories, language]);

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="hidden" flex={1} gap="5" p="5" w="100%" align="center" justify="flex-start">
                        <VStack flex={1} px={2} w="100%" h="80vh" position="relative" overflow="auto">
                            <Field
                                label={t('wrappers.mandatoryLabel', { value: t('objects.category_one') })}
                                errors={formFieldErrors.categoryId}
                                display="flex"
                                alignItems="center"
                                mb="2vh"
                            >
                                <NestedSelect
                                    defaultValue={formData?.categoryId ? [formData?.categoryId] : []}
                                    plainTree={categoriesTree}
                                    placeholder={t('misc.selectACategory')}
                                    onValueChange={(e) => handleInputChange('categoryId', e?.value?.[0] ?? null)}
                                />
                            </Field>
                            <Field
                                label={t('wrappers.nonMandatoryLabel', { value: t('fields.order') })}
                                errors={formFieldErrors.sortOrder}
                                display="flex"
                                alignItems="center"
                                mb="2vh"
                            >
                                <NumberInput.Root
                                    value={formData.sortOrder}
                                    precision={0}
                                    step={1}
                                    onValueChange={(e) => handleInputChange('sortOrder', e.valueAsNumber)}
                                >
                                    <NumberInput.Input />
                                    <NumberInput.Control>
                                        <NumberInput.IncrementTrigger />
                                        <NumberInput.DecrementTrigger />
                                    </NumberInput.Control>
                                </NumberInput.Root>
                            </Field>
                            <LanguageField
                                id="title"
                                errors={formFieldErrors.title}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.title') })}
                                defaultLanguage={defaultLanguage}
                                renderElement={(lang) => <Input
                                    value={formData.title[lang]}
                                    onChange={(e) => {
                                        handleInputChange('title', {...formData.title, [lang]: e?.target?.value ?? ''})
                                    }}
                                    placeholder={t('resources.resourceTitle')}
                                />}
                            />
                            <LanguageField
                                id="content"
                                errors={formFieldErrors.content}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.content') })}
                                defaultLanguage={defaultLanguage}
                                renderElement={(lang) => <RichText
                                    initialValue={formData.content[lang]}
                                    onChange={(e) => {
                                        /** Allow to set new content based on previousData without passing formData, that will cause re-render */
                                        handleInputChange('content', (prevData) => ({...prevData.content, [lang]: e}))
                                    }}
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        overflow: 'hidden'
                                    }}
                                    className={"scrollable"}
                                    placeholder={t('resources.resourceContent')}
                                />}
                            />
                            <FilesInput
                                accept="image/*,.txt,.md,.csv,.json,.pdf,text/plain,application/pdf"
                                deleteText={t('sentences.fileDeletion')}
                                files={formData.files}
                                onChange={(files) => handleInputChange('files', files)}
                                w="100%"
                            />
                        </VStack>
                        {resource?.deletedAt && (
                            <Checkbox.Root
                                alignSelf="flex-start"
                                checked={removeDeletedAt}
                                onCheckedChange={(e) => setRemoveDeletedAt(() => !!e.checked)}
                            >
                                <Checkbox.HiddenInput/>
                                <Checkbox.Control/>
                                <Checkbox.Label>{t('resources.publishResource')}</Checkbox.Label>
                            </Checkbox.Root>
                        )}
                    </VStack>
                    :
                    <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg"/>
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (removeDeletedAt) {
                        data.deletedAt = null;
                    }
                    if (resourceId) {
                        return api.ApiService.update('resource', resourceId, data);
                    }
                    return api.ApiService.create('resource', data);
                }, props?.successCallback)
                }
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditResource;
